import * as React from "react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

/** The heading levels an empty state's title may be rendered at. */
export type EmptyStateLevel = "h2" | "h3" | "h4" | "h5" | "h6";

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: LucideIcon;
  title: string;
  message?: React.ReactNode;
  action?: React.ReactNode;
  /**
   * Heading level for `title`. Defaults to `h3`, which is right wherever the
   * empty state stands in for content inside an already-headed section (the
   * common case, and every existing call site).
   *
   * Pass `h2` when the empty state is the first heading under the page's `h1`,
   * as it is on /search when nothing matched. Leaving it at `h3` there skips a
   * level, which axe reports as `heading-order` and which a screen reader
   * announces as a missing section. Same reasoning, and same additive shape, as
   * `as` on CardTitle.
   */
  as?: EmptyStateLevel;
}

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ icon: Icon, title, message, action, className, as: Heading = "h3", ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-col items-center justify-center text-center gap-3 py-12 px-4",
        className,
      )}
      {...props}
    >
      {Icon && (
        <div className="rounded-full bg-muted p-3">
          <Icon className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
        </div>
      )}
      <div className="space-y-1">
        {/* The size is pinned to the class rather than left to the element.
            globals.css sets a base scale per heading level (h3 is text-base, h2
            is text-xl md:text-2xl), and this heading carried no class of its own,
            so raising the LEVEL for the document outline would have silently
            enlarged the text too. These are the values the base layer gave the
            h3 every existing call site renders, so nothing moves visually and
            `as` now changes only the semantics, which is the whole point of it. */}
        <Heading className="text-base font-semibold">{title}</Heading>
        {message && (
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">{message}</p>
        )}
      </div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  ),
);
EmptyState.displayName = "EmptyState";
